import type { ModuleMeta } from '../../core/types';
import { APP_INSTANCE_CAPABILITIES } from './capabilities';
import type { AppInstanceFieldKey } from './fields';
import { APP_INSTANCE_SKU_MODULE_META } from './resourceConfigs';

function appBinding<K extends (typeof APP_INSTANCE_CAPABILITIES)[number]['key']>(key: K) {
  const binding = APP_INSTANCE_CAPABILITIES.find((item) => item.key === key);
  if (!binding) {
    throw new Error(`AppInstance capability binding not found: ${key}`);
  }
  return binding;
}

/**
 * 模块划分与 SKU `APP_INSTANCE_SKU_MODULE_META` 对齐（参考 CI）：
 * - appService = 镜像/框架（`ApplicationImageSetting`）
 * - envVars / service = 独立勾选模块；镜像赋值时默认勾选、不锁定
 * - storage 无 L1 capability（`AppStorageSetting` / `_appStorageGroups`）
 */
export const APP_INSTANCE_MODULES = [
  {
    key: 'baseInfo',
    title: '基本信息',
    capabilities: [appBinding('base-info')],
    fields: ['base.name', 'base.description', 'base.workspace'],
    presetCheckFields: ['alias'],
  },
  {
    key: 'runtimeConfig',
    title: '运行配置',
    capabilities: [appBinding('runtime-config')],
    fields: ['app.runtime.type', 'app.runtime.workload'],
    presetCheckFields: ['runtime.type'],
    resetFieldPaths: ['runtime'],
  },
  {
    key: 'appService',
    title: '应用镜像',
    capabilities: [appBinding('maas-system-config')],
    fields: ['app.framework', 'app.inferenceSystem'],
    presetCheckFields: APP_INSTANCE_SKU_MODULE_META.appService.presetCheckFields,
    resetFieldPaths: [
      'maasInferenceSource',
      '_inferenceSystem',
      '_imageContainerDeployMountPath',
      '_imageContainerDeployStartup',
      '_imageContainerDeployInstanceScheduling',
      '_imageContainerDeployEnvVars',
      '_imageContainerDeployApis',
      '_imageStorageVolumes',
    ],
  },
  {
    key: 'envVars',
    title: '公共环境变量',
    capabilities: [appBinding('env-vars')],
    fields: ['app.commonEnv'],
    presetCheckFields: APP_INSTANCE_SKU_MODULE_META.envVars.presetCheckFields,
    resetFieldPaths: ['commonEnv'],
  },
  {
    key: 'nodeGroups',
    title: '节点配置',
    capabilities: [appBinding('maas-node-groups')],
    fields: ['app.runMode', 'app.masterNodes', 'app.workerNodes'],
    resetFieldPaths: [
      'runMode',
      'mode',
      'masterNodes',
      'workerNodes',
      'master',
      'worker',
      'master.systemDiskSize',
      'worker.systemDiskSize',
      'masterSystemDiskPricing',
      'workerSystemDiskPricing',
    ],
  },
  {
    key: 'healthCheck',
    title: '探活',
    capabilities: [appBinding('health-check')],
    fields: ['app.healthCheck'],
    presetCheckFields: ['_containerDeployHealthCheck'],
    resetFieldPaths: ['_containerDeployHealthCheck'],
  },
  {
    key: 'service',
    title: '服务配置',
    capabilities: [appBinding('service')],
    fields: ['app.inferenceService'],
    presetCheckFields: APP_INSTANCE_SKU_MODULE_META.service.presetCheckFields,
    resetFieldPaths: ['_inferenceService', '_serviceMountGroupName'],
  },
  {
    key: 'storage',
    title: '应用存储',
    capabilities: [],
    fields: ['app.appStorageGroups', 'app.dataMountPath'],
    presetCheckFields: APP_INSTANCE_SKU_MODULE_META.storage.presetCheckFields,
    resetFieldPaths: ['_appStorageGroups', 'dataMountPath', 'appStorageConfigured'],
  },
] as const satisfies readonly ModuleMeta<AppInstanceFieldKey>[];

export const APP_INSTANCE_MODULE_MAP = Object.fromEntries(APP_INSTANCE_MODULES.map((item) => [item.key, item]));
